import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import { ConfigProvider, theme as antdTheme, Dropdown } from "antd";
import {
  AppstoreOutlined,
  BookOutlined,
  ReadOutlined,
  SafetyCertificateOutlined,
  CustomerServiceOutlined,
  WalletOutlined,
  UserOutlined,
  HeartOutlined,
  LogoutOutlined,
  MenuOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { fetchSelfLearnerProfile } from "PlatForms/SelfLMS/Services/SelfLearnerServices/SelfLearnerProfileService";
import "../self.css";

const NAV = [
  { key: "dashboard", label: "Dashboard", to: "/selfLearner-dashboard", icon: <AppstoreOutlined />, exact: true },
  { key: "available", label: "Available Courses", to: "/selfLearner-dashboard/available-courses", icon: <BookOutlined /> },
  { key: "enrolled", label: "Enrolled Courses", to: "/selfLearner-dashboard/enrolled-courses", icon: <ReadOutlined /> },
  { key: "credentials", label: "Credentials", to: "/selfLearner-dashboard/credentials", icon: <SafetyCertificateOutlined /> },
  { key: "expert", label: "Expert Services", to: "/selfLearner-dashboard/purchased-expert-services", icon: <CustomerServiceOutlined /> },
  { key: "ledger", label: "Payment Ledger", to: "/selfLearner-dashboard/payment-ledger", icon: <WalletOutlined /> },
];

// Ant Design light theme (warm cream + antique gold), scoped to the Self-Learner area.
const antTheme = {
  algorithm: antdTheme.defaultAlgorithm,
  token: {
    colorPrimary: "#9A6B0E",
    colorInfo: "#9A6B0E",
    colorBgBase: "#FAF8F3",
    colorBgContainer: "#FFFEFB",
    colorBgElevated: "#FFFFFF",
    colorBorder: "#E7E1D5",
    colorBorderSecondary: "#EFEADF",
    colorText: "#1C1917",
    colorTextSecondary: "#57534E",
    borderRadius: 10,
    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    fontSize: 14,
  },
  components: {
    Card: { colorBgContainer: "#FFFEFB" },
    Table: { colorBgContainer: "#FFFEFB", headerBg: "#F6F3EC" },
    Modal: { contentBg: "#FFFEFB", headerBg: "#FFFEFB" },
  },
};

function pageMeta(pathname) {
  if (pathname.includes("/available-courses")) return { title: "Available Courses", crumb: "Browse the catalog" };
  if (pathname.includes("/enrolled-courses")) return { title: "Enrolled Courses", crumb: "Your active learning" };
  if (pathname.includes("/credentials") || pathname.includes("/certificate")) return { title: "Credentials", crumb: "Your certificates" };
  if (pathname.includes("expert-services")) return { title: "Expert Services", crumb: "1:1 expert support" };
  if (pathname.includes("payment-ledger")) return { title: "Payment Ledger", crumb: "Purchases and receipts" };
  if (pathname.includes("selfLearner-profile")) return { title: "Profile", crumb: "Your account" };
  if (pathname.includes("personal-interest")) return { title: "Focus Areas", crumb: "Personalize recommendations" };
  return { title: "Dashboard", crumb: "Your learning at a glance" };
}

const SelfShell = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState("");

  useEffect(() => {
    let active = true;
    const token = localStorage.getItem("token");
    fetchSelfLearnerProfile(token)
      .then((res) => {
        const img = res?.data?.profileImage;
        if (active && img) setAvatarUrl(`${process.env.REACT_APP_URL}${img}`);
        else if (active) setAvatarUrl("");
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const isActive = (item) =>
    item.exact
      ? location.pathname === item.to || location.pathname === "/"
      : location.pathname.startsWith(item.to);

  const go = (to) => {
    navigate(to);
    setOpen(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    window.dispatchEvent(new Event("storage"));
    navigate("/login");
  };

  const { title, crumb } = pageMeta(location.pathname);

  const menuItems = [
    { key: "profile", icon: <UserOutlined />, label: "Profile", onClick: () => navigate("/selfLearner-profile") },
    { key: "interest", icon: <HeartOutlined />, label: "Focus Areas", onClick: () => navigate("/personal-interest") },
    { type: "divider" },
    { key: "logout", icon: <LogoutOutlined />, label: "Log out", danger: true, onClick: handleLogout },
  ];

  return (
    <ConfigProvider theme={antTheme}>
      <div className={`sl-root ${open ? "is-open" : ""}`}>
        <div className="sl-shell">
          <aside className="sl-sidebar">
            <button
              type="button"
              className="sl-sidebar__brand"
              onClick={() => go("/selfLearner-dashboard")}
              style={{ background: "none", border: "none", cursor: "pointer", width: "100%" }}
            >
              <img src={`${process.env.PUBLIC_URL}/logo-icon.png`} alt="CoreIQPro" />
              <span className="sl-sidebar__word">
                Core<span className="o">IQ</span>Pro
              </span>
            </button>
            <div className="sl-sidebar__label">Learn</div>
            <nav className="sl-sidebar__nav">
              {NAV.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`sl-nav-item ${isActive(item) ? "is-active" : ""}`}
                  onClick={() => go(item.to)}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              ))}
            </nav>
            <div className="sl-sidebar__foot">
              <div className="sl-sidebar__upsell">
                <h4>Keep your streak</h4>
                <p>Pick up where you left off and earn your next certificate.</p>
              </div>
            </div>
          </aside>

          <div className="sl-backdrop" onClick={() => setOpen(false)} />

          <div className="sl-main">
            <header className="sl-topbar">
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <button className="sl-burger" type="button" onClick={() => setOpen(true)} aria-label="Open menu">
                  <MenuOutlined />
                </button>
                <div>
                  <div className="sl-topbar__title">{title}</div>
                  <div className="sl-topbar__crumb">{crumb}</div>
                </div>
              </div>
              <div className="sl-topbar__right">
                <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
                  <button className="sl-userbtn" type="button">
                    <span className="sl-userbtn__av">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt=""
                          style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
                        />
                      ) : (
                        <UserOutlined />
                      )}
                    </span>
                    <span>Account</span>
                    <DownOutlined style={{ fontSize: 10, opacity: 0.6 }} />
                  </button>
                </Dropdown>
              </div>
            </header>
            <main className="sl-content">{children}</main>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

SelfShell.propTypes = {
  children: PropTypes.node,
};

export default SelfShell;
